import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
} from 'react';
import { ThemeProvider as StyledThemeProvider } from 'styled-components';
import useMediaQuery from '@/hooks/useMediaQuery';
import { darkTheme, lightTheme, Theme } from './themes';
import { MEDIA_BREAKPOINTS } from '@/constants/layout';

export const ThemeContext = createContext<{
  theme: Theme;
  mode: 'dark' | 'light';
  isDesktop: boolean;
  toggleTheme: () => void;
}>({
  theme: darkTheme,
  mode: 'dark',
  isDesktop: false,
  toggleTheme: () => {},
});

export const ThemeProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [theme, setTheme] = useState<Theme>(darkTheme);
  const [mode, setMode] = useState<'dark' | 'light'>('dark');
  const isDesktop = useMediaQuery(`(min-width: ${MEDIA_BREAKPOINTS.mediumScreen})`);

  const toggleTheme = () => {
    setTheme(prevTheme => (prevTheme === darkTheme ? lightTheme : darkTheme));
    setMode(prevMode => (prevMode === 'dark' ? 'light' : 'dark'));
  };

  return (
    <ThemeContext.Provider value={{ theme, mode, isDesktop, toggleTheme }}>
      <StyledThemeProvider theme={theme}>{children}</StyledThemeProvider>
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};
